import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Coins, Plus, Receipt } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/layout/AppShell';
import { PurchaseRow } from '@/components/lists';
import { Page, Rise } from '@/components/motion';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { EmptyState, ErrorState, Pager, Tabs } from '@/components/ui/misc';
import { coinsApi } from '@/lib/api/endpoints';
import type { PurchaseStatus } from '@/lib/api/types';
import { ListSkeleton } from './ChecksPage';

const LIMIT = 20;
export const PURCHASE_FILTERS: { value: PurchaseStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'declined', label: 'Declined' },
];

export function PurchasesPage() {
  const [status, setStatus] = React.useState<PurchaseStatus | 'all'>('all');
  const [offset, setOffset] = React.useState(0);
  const params = { status: status === 'all' ? undefined : status, limit: LIMIT, offset };
  const q = useQuery({
    queryKey: ['purchases', params],
    queryFn: () => coinsApi.purchases(params),
    placeholderData: keepPreviousData,
    // A pending purchase may be approved while the page is open.
    refetchInterval: (query) => (query.state.data?.some((p) => p.status === 'pending') ? 20_000 : false),
  });

  return (
    <Page>
      <PageHeader
        eyebrow="Coins"
        title="My purchases"
        description="Receipts you've sent and their review status."
        actions={
          <Button asChild variant="brand">
            <Link to="/coins/buy">
              <Plus /> Buy coins
            </Link>
          </Button>
        }
      />
      <Rise className="mb-4">
        <Tabs layoutId="purchase-filter" value={status} onChange={(v) => { setStatus(v); setOffset(0); }} options={PURCHASE_FILTERS} />
      </Rise>
      <Rise>
        <Card className="overflow-hidden">
          {q.isPending ? (
            <ListSkeleton />
          ) : q.isError ? (
            <ErrorState error={q.error} onRetry={() => q.refetch()} />
          ) : q.data.length === 0 ? (
            <EmptyState
              icon={<Receipt />}
              title="No purchases"
              description="When you buy coins, your receipts show up here."
              action={
                <Button asChild variant="brand">
                  <Link to="/coins/buy">
                    <Coins /> Buy coins
                  </Link>
                </Button>
              }
            />
          ) : (
            <div className="divide-y">
              {q.data.map((p) => (
                <PurchaseRow key={p.id} purchase={p} />
              ))}
            </div>
          )}
          <Pager offset={offset} limit={LIMIT} count={q.data?.length ?? 0} onChange={setOffset} loading={q.isFetching} />
        </Card>
      </Rise>
    </Page>
  );
}
